import { Award, CheckCircle2, Download } from 'lucide-react'
import PropTypes from 'prop-types'

import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Skeleton } from '@/components/elements/skeleton'
import { cn } from '@/utils/helpers'

import CourseProgressBar from './CourseProgressBar'
import ModuleNavigator from './ModuleNavigator'

const STATUS_VARIANT = {
  'Not Started': 'secondary',
  'In Progress': 'warning',
  'Quiz Pending': 'warning',
  Completed: 'success',
}

/**
 * Shared cert hero for the student course pages — the one place that frames
 * certificate vs module, in both the in-progress (CourseOverview) and
 * completed (CourseComplete) states, so navigating between sibling modules
 * doesn't flip the mental model.
 *
 * Hierarchy: certificate as eyebrow, current module as headline, a segmented
 * module bar as the single expression of position-within-cert. Single-module
 * certs collapse to just the module headline (nothing to navigate, no
 * position to show). A fully-issued cert promotes the headline to
 * "Certification Complete" — the only place the two states diverge.
 */
function CertHero({
  courseRecord,
  entityId,
  variant,
  stepStats = null,
  stepStatsPending = false,
  isInstructorView = false,
  className = '',
}) {
  const certificate = courseRecord.studentCertificate?.certificate
  const siblings = courseRecord.studentCertificate?.courseRecords ?? []
  const currentIndex = siblings.findIndex((r) => r.id === courseRecord.id)
  const totalModules = siblings.length
  const completedModules = siblings.filter((r) => r.status === 'Completed').length
  const isMultiModule = totalModules > 1 && currentIndex !== -1

  const isComplete = variant === 'complete'
  const certComplete = isComplete && totalModules > 0 && completedModules === totalModules
  // The cert PDF is generated server-side via signal on issuance — it's only
  // present when the StudentCertificate is fully issued (all modules done +
  // any other gating like training event / proficiency met).
  const certDownloadUrl = courseRecord.studentCertificate?.document?.signedFilePath

  const status = isComplete ? 'Completed' : courseRecord.status || 'Not Started'
  const Icon = certComplete || !isComplete ? Award : CheckCircle2

  const ariaLabel = (() => {
    if (isComplete) return 'Course completion'
    // Standalone courses have no certificate context to announce.
    return certificate ? 'Certification context' : 'Module status'
  })()

  return (
    <section
      aria-label={ariaLabel}
      className={cn(
        // Pin the hero's width here rather than inheriting the page
        // container's: CourseOverview constrains its whole article to
        // max-w-3xl while CourseComplete runs full width, and the hero
        // should read identically when navigating between the two states.
        'mx-auto flex w-full max-w-3xl flex-col gap-4 rounded-lg border p-5',
        isComplete
          ? 'animate-in fade-in slide-in-from-top-2 border-success/30 bg-success/5 duration-500'
          : 'border-primary/20 bg-primary/5',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'rounded-full p-2',
            isComplete ? 'bg-success/15' : 'bg-primary/10',
            certComplete && 'animate-in zoom-in-50 duration-700',
          )}
        >
          <Icon
            className={cn('h-5 w-5', isComplete ? 'text-success' : 'text-primary')}
            aria-hidden="true"
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {certificate && isMultiModule && !certComplete && (
            <>
              <p className="text-muted-foreground text-xs tracking-wide uppercase">
                Working toward
              </p>
              <p className="leading-tight font-semibold">{certificate.name}</p>
            </>
          )}

          {certComplete ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl leading-tight font-bold">Certification Complete</h2>
                <Badge variant="success" size="default">
                  Completed
                </Badge>
              </div>
              {certificate && (
                <p className="text-muted-foreground text-sm">
                  You finished every module for {certificate.name}.
                </p>
              )}
            </>
          ) : (
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              <h2 className="text-xl leading-tight font-bold">{courseRecord.course.name}</h2>
              <Badge variant={STATUS_VARIANT[status] || 'secondary'} size="default">
                {status}
              </Badge>
            </div>
          )}

          {!isComplete && stepStats && stepStats.total > 0 && (
            <CourseProgressBar
              current={stepStats.completed}
              total={stepStats.total}
              label={`${stepStats.completed} of ${stepStats.total} steps complete`}
              tone="on-light"
              className="mt-2 max-w-md"
            />
          )}
          {/* Same footprint as the progress bar (text-xs label + h-1.5 track)
              so the hero doesn't grow and shift the page when the steps
              fetch resolves. */}
          {!isComplete && !stepStats && stepStatsPending && (
            <div className="mt-2 flex max-w-md flex-col gap-1" aria-hidden="true">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-1.5 w-full rounded-full" />
            </div>
          )}

          {isMultiModule && (
            <ModuleSegmentBar
              siblings={siblings}
              currentIndex={currentIndex}
              className="mt-2 max-w-md"
            />
          )}
        </div>
      </div>

      {isMultiModule && !isInstructorView && (
        <ModuleNavigator courseRecord={courseRecord} entityId={entityId} />
      )}

      {isComplete && certDownloadUrl && !isInstructorView && (
        <div>
          <Button asChild size="lg">
            <a
              href={certDownloadUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Download ${certificate?.name ?? 'certificate'} PDF`}
            >
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Download certificate
            </a>
          </Button>
        </div>
      )}
    </section>
  )
}

CertHero.propTypes = {
  courseRecord: PropTypes.object.isRequired,
  entityId: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['progress', 'complete']).isRequired,
  stepStats: PropTypes.shape({
    completed: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }),
  stepStatsPending: PropTypes.bool,
  isInstructorView: PropTypes.bool,
  className: PropTypes.string,
}

/**
 * Position-within-cert as one glanceable strip: a segment per module,
 * completed = success, the current module marked by height + color when it
 * still has work left. Replaces the "Module N of M" sentence, the
 * "N of M modules toward cert" stat tile, and the module-count subtitle that
 * used to each say this separately.
 */
function ModuleSegmentBar({ siblings, currentIndex, className = '' }) {
  const total = siblings.length
  const completed = siblings.filter((s) => s.status === 'Completed').length

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div
        role="img"
        aria-label={`Module ${currentIndex + 1} of ${total}, ${completed} of ${total} complete`}
        className="flex items-center gap-1"
      >
        {siblings.map((sibling, index) => {
          const isCurrent = index === currentIndex
          const segmentColor = (() => {
            if (sibling.status === 'Completed') return 'bg-success'
            if (isCurrent) return 'bg-primary'
            return 'bg-muted-foreground/20'
          })()
          return (
            <span
              key={sibling.id}
              className={cn('flex-1 rounded-full', isCurrent ? 'h-2.5' : 'h-1.5', segmentColor)}
            />
          )
        })}
      </div>
      <p className="text-muted-foreground text-xs">
        Module {currentIndex + 1} of {total}
      </p>
    </div>
  )
}

ModuleSegmentBar.propTypes = {
  siblings: PropTypes.array.isRequired,
  currentIndex: PropTypes.number.isRequired,
  className: PropTypes.string,
}

export default CertHero
